// components/StringList.tsx

import React from 'react';
import Link from 'next/link';

import {cn} from "@isudio/utils";

export interface ListItem {
  title?: string;
  link?: string;
  href?: string;
  icon?: string;
  handler?: () => void;
}

export enum SocialIcons {
  contact = "fa-duotone fa-light fa-square-envelope",
  linkedIn = "fa-brands fa-linkedin",
  x = "fa-brands fa-x-twitter",
  tumblr = "fa-brands fa-square-tumblr",
  facebook = "fa-brands fa-square-facebook",
  instagram = "fa-brands fa-instagram"
}

interface StringListProps {
  items: (string | ListItem)[];
  linksInNewPage?: boolean;
  hoverColor?: string;
}

const StringList: React.FC<StringListProps> = ({
  items = [],
  linksInNewPage = true,
  hoverColor = "hover:text-generic-hover!"
}) => {
  if (!items || items.length === 0) return null;

  // Detect if this is a "social links" dataset
  const isIconMode = items.some(
    (item) => typeof item !== 'string' && item?.href && item?.icon
  );

  // ✅ ICON MODE (horizontal)
  if (isIconMode) {
    return (
      <div className="flex items-center gap-4 flex-wrap">
        {items.map((item) => {
          if (typeof item === 'string') return null;
          const href = item.href as string;

          return (
            <Link
              className={cn("text-2xl transition duration-200 hover:scale-105", `hover:${hoverColor}`)}
              href={href}
              onClick={item?.handler}
              tabIndex={-1}
            >
              <i className={item?.icon}></i>
            </Link>
          );
        })}
      </div>
    );
  }

  // ✅ DEFAULT LIST MODE
  return (
    <ul className="list-disc list-outside pl-4 space-y-1">
      {items.map((item, index) => {
        if (typeof item === 'string') {
          return <li key={index}>{item}</li>;
        }

        const {title, link} = item;
        const displayText = title || link;

        if (!link) {
          return <li key={index}>{displayText}</li>;
        }

        return (
          <li key={index}>
            <Link
              className="underline transition duration-200 hover:text-generic-hover! hover:scale-105"
              href={link}
              target={linksInNewPage ? "_blank" : undefined}
              rel={linksInNewPage ? "noopener noreferrer" : undefined}
              tabIndex={-1}
            >
              {displayText}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

StringList.displayName = "StringList";
export {StringList};

